import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { documents, buildings } from '../services/api';

const CATEGORIES = ['bylaw', 'contract', 'warranty', 'minutes', 'financial', 'insurance', 'correspondence', 'form_b', 'form_f', 'other'];

const CAT_COLORS = {
  bylaw: 'bg-purple-50 text-purple-700',
  contract: 'bg-blue-50 text-blue-700',
  warranty: 'bg-green-50 text-green-700',
  minutes: 'bg-yellow-50 text-yellow-700',
  financial: 'bg-orange-50 text-orange-700',
  insurance: 'bg-red-50 text-red-700',
  correspondence: 'bg-pink-50 text-pink-700',
  form_b: 'bg-indigo-50 text-indigo-700',
  form_f: 'bg-teal-50 text-teal-700',
  other: 'bg-gray-50 text-gray-600',
};

const CAT_LABELS = { form_b: 'Form B', form_f: 'Form F' };
const catLabel = (c) => CAT_LABELS[c] || (c.charAt(0).toUpperCase() + c.slice(1));

function UploadModal({ buildingList, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ name: '', category: 'other', building_id: '', notes: '', owner_visible: false });
  const [uploading, setUploading] = useState(false);
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const onDrop = useCallback((accepted) => {
    if (accepted[0]) {
      setFile(accepted[0]);
      if (!form.name) setForm((prev) => ({ ...prev, name: accepted[0].name }));
    }
  }, [form.name]);

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, multiple: false,
    accept: { 'application/pdf': [], 'text/plain': [], 'image/*': [],
               'application/msword': [], 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [] },
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error('Please select a file');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', form.name || file.name);
      fd.append('category', form.category);
      if (form.building_id) fd.append('building_id', form.building_id);
      if (form.notes) fd.append('notes', form.notes);
      fd.append('owner_visible', form.owner_visible ? 'true' : 'false');
      await documents.upload(fd);
      toast.success('Document uploaded');
      onUploaded();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="font-semibold text-gray-900">Upload Document</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Dropzone */}
          <div {...getRootProps()} className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
            isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400'}`}>
            <input {...getInputProps()} />
            {file ? (
              <div>
                <p className="text-sm text-gray-700 font-medium">{file.name}</p>
                <p className="text-xs text-gray-400 mt-1">{(file.size / 1024).toFixed(0)} KB</p>
              </div>
            ) : (
              <>
                <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-sm text-gray-500">
                  {isMobile ? 'Tap to choose file' : 'Drop file here or '}
                  {!isMobile && <span className="text-blue-600">browse</span>}
                </p>
                <p className="text-xs text-gray-400 mt-1">PDF, DOC, TXT, images up to 16 MB</p>
              </>
            )}
          </div>

          {/* Mobile: camera shortcut */}
          {isMobile && (
            <label className="flex items-center justify-center gap-2 w-full border border-gray-300 rounded-xl py-3 text-sm text-gray-600 cursor-pointer hover:bg-gray-50 active:bg-gray-100 transition-colors">
              <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Take Photo with Camera
              <input type="file" accept="image/*" capture="environment"
                className="hidden"
                onChange={(e) => { if (e.target.files[0]) onDrop([e.target.files[0]]); }} />
            </label>
          )}
          <div>
            <label className="label">Document Name</label>
            <input className="input" value={form.name} onChange={set('name')} placeholder="Landscaping Contract 2024" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Category</label>
              <select className="input" value={form.category} onChange={set('category')}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{catLabel(c)}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Building</label>
              <select className="input" value={form.building_id} onChange={set('building_id')}>
                <option value="">General / All</option>
                {buildingList.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="label">Notes</label>
            <input className="input" value={form.notes} onChange={set('notes')} placeholder="Optional notes…" />
          </div>
          <label className="flex items-center gap-3 cursor-pointer p-3 bg-blue-50 border border-blue-100 rounded-lg hover:bg-blue-100 transition-colors">
            <div className={`relative w-10 h-6 rounded-full transition-colors ${form.owner_visible ? 'bg-blue-600' : 'bg-gray-300'}`}>
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.owner_visible ? 'translate-x-5' : 'translate-x-1'}`} />
            </div>
            <input type="checkbox" className="hidden" checked={form.owner_visible}
              onChange={(e) => setForm((p) => ({ ...p, owner_visible: e.target.checked }))} />
            <div>
              <p className="text-sm font-medium text-gray-800">Visible to owners</p>
              <p className="text-xs text-gray-500">Owners can view & download this in their portal</p>
            </div>
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
            <button type="submit" disabled={uploading}
              className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50">
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DownloadBadge({ docId }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (data) return;
    setLoading(true);
    try {
      const res = await documents.downloadCount(docId);
      setData(res.data);
    } catch {
      setData({ count: 0, downloads: [] });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      <button onClick={toggle} className="text-xs text-gray-400 hover:text-gray-600 hover:underline">
        {data ? `Downloaded ${data.count}×` : 'Downloads'}
      </button>
      {open && (
        <div className="absolute z-10 left-0 bottom-full mb-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg p-3">
          {loading ? (
            <p className="text-xs text-gray-400">Loading…</p>
          ) : data.downloads.length === 0 ? (
            <p className="text-xs text-gray-400">Not downloaded yet.</p>
          ) : (
            <ul className="space-y-1.5 max-h-40 overflow-y-auto">
              {data.downloads.map((l) => (
                <li key={l.id} className="text-xs text-gray-600">
                  <span className="font-medium capitalize">{l.downloaded_by_type}</span>
                  {l.requester_email ? ` (${l.requester_email})` : ''}
                  <span className="text-gray-400"> — {new Date(l.downloaded_at).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DocumentVault() {
  const [docs, setDocs] = useState([]);
  const [buildingList, setBuildingList] = useState([]);
  const [filterCat, setFilterCat] = useState('');
  const [filterBuilding, setFilterBuilding] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [insights, setInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);

  const load = () => {
    documents.list({ category: filterCat, building_id: filterBuilding })
      .then((r) => setDocs(r.data))
      .catch(() => toast.error('Failed to load documents'));
  };

  useEffect(() => {
    load();
    buildings.list().then((r) => setBuildingList(r.data)).catch(() => {});
  }, [filterCat, filterBuilding]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    await documents.delete(id);
    toast.success('Document deleted');
    load();
  };

  const handleDownload = async (doc) => {
    try {
      const res = await documents.download(doc.id);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename || doc.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const handleToggleOwnerVisible = async (doc) => {
    try {
      await documents.setOwnerVisible(doc.id, !doc.owner_visible);
      toast.success(doc.owner_visible ? 'Hidden from owners' : 'Now visible to owners');
      load();
    } catch {
      toast.error('Failed to update visibility');
    }
  };

  const handleLoadInsights = async () => {
    setLoadingInsights(true);
    try {
      const res = await documents.portfolioInsights();
      setInsights(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to analyze documents');
    } finally {
      setLoadingInsights(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await documents.search(searchQuery);
      setSearchResult(res.data.answer);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Document Vault</h1>
          <p className="text-gray-500 text-sm mt-1">{docs.length} documents</p>
        </div>
        <button onClick={() => setShowUpload(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Upload
        </button>
      </div>

      {/* Smart Document Insights */}
      <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl border border-amber-200 overflow-hidden mb-6">
        <div className="flex items-center justify-between px-5 py-4 border-b border-amber-100">
          <div className="flex items-center gap-2">
            <span className="text-lg">💡</span>
            <h2 className="font-semibold text-gray-900">Smart Document Insights</h2>
            {insights?.urgent?.length > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                {insights.urgent.length} urgent
              </span>
            )}
          </div>
          <button
            onClick={handleLoadInsights}
            disabled={loadingInsights}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium rounded-lg disabled:opacity-50 transition-colors"
          >
            {loadingInsights ? (
              <>
                <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg>
                Analyzing…
              </>
            ) : insights ? '🔄 Re-analyze' : '🔍 Analyze Documents'}
          </button>
        </div>

        {loadingInsights ? (
          <div className="px-5 py-10 text-center text-amber-700 text-sm">
            <svg className="animate-spin w-6 h-6 mx-auto mb-3 text-amber-500" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg>
            AI is scanning your documents for expiry dates, action items, and insights…
          </div>
        ) : insights ? (
          <div>
            {/* Urgent */}
            {insights.urgent?.length > 0 && (
              <div className="px-5 py-4 border-b border-amber-100">
                <h3 className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-2">🔴 Urgent</h3>
                <div className="space-y-2">
                  {insights.urgent.map((item, i) => (
                    <div key={i} className="bg-red-50 border border-red-100 rounded-lg px-3 py-2.5">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-semibold text-red-900">{item.doc_name}</p>
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium shrink-0 ${item.severity === 'critical' ? 'bg-red-200 text-red-800' : 'bg-orange-100 text-orange-700'}`}>
                          {item.severity}
                        </span>
                      </div>
                      <p className="text-xs text-red-700 mt-0.5">{item.issue}</p>
                      {item.action_needed && (
                        <p className="text-xs text-red-600 mt-1 font-medium">→ {item.action_needed}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Needed */}
            {insights.action_needed?.length > 0 && (
              <div className="px-5 py-4 border-b border-amber-100">
                <h3 className="text-xs font-semibold text-orange-700 uppercase tracking-wide mb-2">⚠️ Action Needed</h3>
                <div className="space-y-2">
                  {insights.action_needed.map((item, i) => (
                    <div key={i} className="flex items-start gap-3 py-1">
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium shrink-0 ${
                        item.priority === 'high' ? 'bg-red-100 text-red-700' :
                        item.priority === 'medium' ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>{item.priority}</span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900">{item.action}</p>
                        <p className="text-xs text-gray-500 truncate">{item.doc_name} · {item.reason}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Insights */}
            {insights.insights?.length > 0 && (
              <div className="px-5 py-4 border-b border-amber-100">
                <h3 className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">💡 Insights</h3>
                <div className="space-y-2">
                  {insights.insights.map((item, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium shrink-0 mt-0.5 ${
                        item.category === 'cost' ? 'bg-green-100 text-green-700' :
                        item.category === 'risk' ? 'bg-red-100 text-red-700' :
                        item.category === 'compliance' ? 'bg-blue-100 text-blue-700' :
                        'bg-purple-100 text-purple-700'
                      }`}>{item.category}</span>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{item.title}</p>
                        <p className="text-xs text-gray-500">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty / summary */}
            {!insights.urgent?.length && !insights.action_needed?.length && !insights.insights?.length ? (
              <div className="px-5 py-6 text-center text-sm text-gray-500">
                ✅ {insights.summary || 'No issues found in your documents.'}
              </div>
            ) : (
              <div className="px-5 py-3 bg-amber-50/60 text-xs text-amber-700 flex items-center gap-2">
                <span>{insights.summary}</span>
                <span className="ml-auto text-amber-500">{insights.analyzed_count || insights.total_scanned} docs scanned</span>
              </div>
            )}
          </div>
        ) : (
          <div className="px-5 py-6 text-center text-sm text-gray-500">
            <p>Click "Analyze Documents" to scan for expiry dates, action items, and insights.</p>
            <p className="text-xs text-gray-400 mt-1">Works best with text-based PDFs and contracts.</p>
          </div>
        )}
      </div>

      {/* AI Search */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100 p-5 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          <span className="font-semibold text-blue-900 text-sm">AI Document Search</span>
        </div>
        <form onSubmit={handleSearch} className="flex gap-2">
          <input
            className="flex-1 border border-blue-200 rounded-lg px-3 py-2 text-sm bg-white outline-none focus:border-blue-500"
            placeholder='e.g. "What is our landscaping contract rate?" or "When does the elevator warranty expire?"'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button type="submit" disabled={searching}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 whitespace-nowrap">
            {searching ? 'Searching…' : 'Ask AI'}
          </button>
        </form>
        {searchResult && (
          <div className="mt-3 p-3 bg-white rounded-lg border border-blue-100 text-sm text-gray-700">
            <p className="font-medium text-blue-700 mb-1">Answer:</p>
            <p className="whitespace-pre-wrap">{searchResult}</p>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <select className="input w-44" value={filterCat} onChange={(e) => setFilterCat(e.target.value)}>
          <option value="">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
        </select>
        <select className="input w-48" value={filterBuilding} onChange={(e) => setFilterBuilding(e.target.value)}>
          <option value="">All buildings</option>
          {buildingList.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>

      {/* Document grid */}
      {docs.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 py-16 text-center text-gray-400">
          <p className="text-lg">No documents yet.</p>
          <button onClick={() => setShowUpload(true)} className="mt-2 text-sm text-blue-600 hover:underline">
            Upload your first document →
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {docs.map((d) => (
            <div key={d.id} className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 text-sm truncate">{d.name}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CAT_COLORS[d.category] || CAT_COLORS.other}`}>
                      {catLabel(d.category)}
                    </span>
                    {d.building_name && <span className="text-xs text-gray-400">{d.building_name}</span>}
                  </div>
                  {d.file_size && <p className="text-xs text-gray-400 mt-1">{formatBytes(d.file_size)}</p>}
                </div>
              </div>
              <div className="mt-3 border-t pt-3">
                {/* Owner visible toggle */}
                <button
                  onClick={() => handleToggleOwnerVisible(d)}
                  className={`flex items-center gap-2 w-full px-2 py-1.5 rounded-lg text-xs font-medium transition-colors mb-2 ${
                    d.owner_visible
                      ? 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                      : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                  }`}>
                  <div className={`relative w-7 h-4 rounded-full transition-colors shrink-0 ${d.owner_visible ? 'bg-blue-600' : 'bg-gray-300'}`}>
                    <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform ${d.owner_visible ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
                  </div>
                  {d.owner_visible ? '👁 Visible to owners' : 'Hidden from owners'}
                </button>
                <div className="flex items-center gap-3">
                  <button onClick={() => handleDownload(d)}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:underline font-medium">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download
                  </button>
                  <span className="text-gray-200">|</span>
                  <button onClick={() => handleDelete(d.id)}
                    className="text-xs text-red-500 hover:underline">Delete</button>
                  <span className="text-gray-200">|</span>
                  <DownloadBadge docId={d.id} />
                </div>
                <p className="text-xs text-gray-400 mt-2">{new Date(d.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {showUpload && (
        <UploadModal
          buildingList={buildingList}
          onClose={() => setShowUpload(false)}
          onUploaded={() => { setShowUpload(false); load(); }}
        />
      )}

      <style>{`
        .label { display: block; font-size: 0.75rem; font-weight: 500; color: #374151; margin-bottom: 0.25rem; }
        .input { width: 100%; border: 1px solid #d1d5db; border-radius: 0.5rem; padding: 0.5rem 0.75rem; font-size: 0.875rem; outline: none; }
        .input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.1); }
      `}</style>
    </div>
  );
}
